import React, { useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useMediaQuery } from "@mui/material";

import { PageContainer } from "@ui/common";

import { CatalogTab, catalogueReducer, ECatalogue } from "@pages/Catalog/components/utils";

import { useFilters } from "@hooks/useFiltersQuery";
import { adaptFiltersForDisplay } from "@hooks/useFiltersQuery/utils";

import { categoriesFilterButtons, quickFilterById, quickFiltersCategories } from "@store/actions/quickFilters";
import { getAllTracks } from "@store/actions/track";
import { resetAccumulatedTracks, restoreAccumulatedTracks, setIsLoading } from "@store/reducers/catalogue";
import { setAvailableTagsState } from "@store/reducers/filters";
import { getAccumulatedTracks } from "@store/selectors/getCatalogueSelector";
import { getQuickFiltersSelector } from "@store/selectors/quickFilters";

import { CustomHelmet } from "@components/CustomHelmet";
import { FAQ } from "@components/FAQ/FAQ";
import { QuestionFlowDialog } from "@components/QuestionFlowDialog";
import { setActiveSearch, trackSearch } from "@utils/analytics";
import { scrollToAnchorByRef } from "@utils/scrollToAnchor";
import { useQueryParam } from "use-query-params";

import CatalogueBGDesktop from "@/assets/avif/catalog-desktop.avif";
import CatalogueBGMobile from "@/assets/avif/common-mobile.avif";
import { useIsDesktop } from "@/hooks/layout";
import { ICatalogue } from "@/models/models";
import Dialog from "@/pages/Catalog/components/Dialog";
import { catalogue, catalogueFilters } from "@/store/actions/catalogue";
import { availableTags, tags } from "@/store/actions/filters";
import { useAppDispatch, useAppDispatchUnwrap, useAppSelector } from "@/store/hooks";
import { getCatalogueData, getTracksData, isLoadingCatalogue } from "@/store/selectors/getCatalogueSelector";
import { getTagsSelector } from "@/store/selectors/getFiltersSelector";
import { ERoutes, REGEX_EXP_FOR_BPM, REGEX_EXP_FOR_LENGTH, sortValues } from "@/utils/constants";
import { getShuffleSeed, seededHash } from "@/utils/shuffleSeed";

import { MainContentDesktop } from "./components/desktop";
import { MainContentMobile } from "./components/mobile";
import { CatalogTabDesktop } from "./components/Tabs/CatalogTabDesktop";
import { CatalogTabMobile } from "./components/Tabs/CatalogTabMobile";
import { CatalogPageContext } from "./context";
import { CatalogMediaLimit } from "./enums";
import { buildUrl, getSort } from "./utils";

export enum CatalogLocalStorage {
  CATALOG_TAB = "as:catalogTab",
  HINT_CLOSED = "as:isHintClosed",
  FILTERS_SECTION = "as:filtersSection",
  ACCUMULATED_TRACKS = "as:accumulatedTracks",
  LAST_PAGE = "as:lastPage",
  LAST_SORT = "as:lastSort",
}

const Catalogue = () => {
  const { t } = useTranslation(["catalogue", "downloadModal", "questionFlow", "homePage", "toasts"]);
  const { t: tC } = useTranslation(["catalogue"]);

  const isDesktop = useIsDesktop();
  // Header swaps at 1150 (mobile header is sticky/in-flow below it); the fixed
  // desktop header's 80px top clearance must key off 1150, not md=1024, else
  // 1024–1149 double-reserves and pushes the catalog down. Content layout still
  // switches at 1024 (isDesktop) — only the top padding follows the header.
  const isHeaderDesktop = useMediaQuery("(min-width:1150px)");
  const navigate = useNavigate();

  const dispatch = useAppDispatchUnwrap();
  const syncDispatch = useAppDispatch();

  const location = useLocation();
  const { id } = useParams();

  const [jump] = useQueryParam("jump");
  const [qParam, setQParam] = useQueryParam("q");
  const [tParam] = useQueryParam("t");
  const { handleSetQuery, query } = useFilters();

  const [state, dispatchReducer] = useReducer(catalogueReducer, {
    isFiltersOpen: query.isFiltersOpen || JSON.parse(localStorage.getItem(CatalogLocalStorage.FILTERS_SECTION)),
    isFavoritesButtonClicked: false,
    filters: [],
    search: "",
    // Sort source of truth: URL ?sort= → last session sort (sessionStorage) →
    // default. The sessionStorage fallback keeps the selection across back/forward
    // nav even if the URL param doesn't survive the round-trip.
    sortBy: getSort(query.sort) || getSort(sessionStorage.getItem(CatalogLocalStorage.LAST_SORT) || "") || sortValues(t)[0],
    catalogTab: qParam ? CatalogTab.TRACK : (query.isAlbumsTab || JSON.parse(localStorage.getItem(CatalogLocalStorage.CATALOG_TAB))),
    openQuestionDialog: false,
    openFilterDialog: false,
  });
  const [page, setPage] = useState<number>(query.pageNumber || 1);
  // Drives ONLY the search-box spinner — true while a smart-search request is in
  // flight, never for tab toggles / pagination (those use isCatalogueLoading).
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [searchAnchorEl, setSearchAnchorEl] = useState<null | HTMLElement>(null);
  const [submittedSearch, setSubmittedSearch] = useState<string>("");
  const [noResultsQuery, setNoResultsQuery] = useState<string>("");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [localSearch, setLocalSearch] = useState<string>("");
  const allSmartResultsRef = useRef<any[]>([]);
  const [smartAlbums, setSmartAlbums] = useState<ICatalogue[]>([]);
  const [smartResultsPage, setSmartResultsPage] = useState(1);
  const SMART_PAGE_SIZE = 20;

  const { pageCount } = useAppSelector(getCatalogueData);
  const { pageCount: tracksPageCount } = useAppSelector(getTracksData);
  const { quickFiltersCategories: quickCategories, quickFilter } = useAppSelector(getQuickFiltersSelector);

  const tagState = useAppSelector(getTagsSelector);

  const isCatalogueLoading = useAppSelector(isLoadingCatalogue);
  const accumulatedTracks = useAppSelector(getAccumulatedTracks);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const catalogRef = useRef<HTMLElement>(null);
  const shouldJump = useRef(false);
  const isToggleBetween = useRef(false);

  // Deep-link: /music?test=true opens the free-use questionnaire (used by the
  // post-signup Welcome modal's "Take the quick test" CTA). Strip the param
  // afterwards so a reload / back-nav doesn't re-open it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const test = params.get("test");
    if (test && test !== "0" && test !== "false") {
      dispatchReducer({ type: ECatalogue.OPEN_QUESTION_FLOW, payload: true });
      params.delete("test");
      navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore accumulated tracks from sessionStorage on mount
  useEffect(() => {
    try {
      const savedTracks = sessionStorage.getItem(CatalogLocalStorage.ACCUMULATED_TRACKS);
      const savedPage = sessionStorage.getItem(CatalogLocalStorage.LAST_PAGE);
      if (savedTracks) {
        const parsed = JSON.parse(savedTracks);
        if (Array.isArray(parsed) && parsed.length > 0) {
          syncDispatch(restoreAccumulatedTracks(parsed));
          if (savedPage) {
            const pageNum = parseInt(savedPage, 10);
            if (pageNum > 1) {
              setPage(pageNum);
              handleSetQuery({ pageNumber: pageNum });
            }
          }
        }
      }
    } catch (_e) {
      // ignore parse errors
    }
  }, []);

  // Save accumulated tracks to sessionStorage when they change
  useEffect(() => {
    if (accumulatedTracks.length > 0) {
      try {
        sessionStorage.setItem(CatalogLocalStorage.ACCUMULATED_TRACKS, JSON.stringify(accumulatedTracks));
        sessionStorage.setItem(CatalogLocalStorage.LAST_PAGE, String(page));
      } catch (_e) {
        // ignore storage errors
      }
    }
  }, [accumulatedTracks, page]);

  useEffect(() => {
    const getCatalogueFilters = async () => {
      await dispatch(catalogueFilters());
    };

    const getTags = async () => {
      await dispatch(tags());
    };

    getCatalogueFilters();
    getTags();
    dispatchReducer({
      type: ECatalogue.Filters,
      payload: adaptFiltersForDisplay(query, tagState),
    });
  }, []);

  useEffect(() => {
    if (location.pathname.includes(ERoutes.QuickFilter)) {
      dispatch(quickFilterById(id));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (quickFilter && quickFilter.tags) {
      handleSetQuery({ tags: quickFilter.tags.map((tag) => tag._id) });
    }
  }, [quickFilter]);

  // Skip the initial mount. Writing the sort into the URL on load pushes a
  // SECOND history entry — the merge carries the default pageNumber /
  // isFiltersOpen / isAlbumsTab, so landing via an artist link (which already
  // created the `?q=…` entry) immediately stacked `?q=…&sort=…&pageNumber=1&…`
  // on top, and Back then needed two clicks to leave the results. The sort is
  // already applied from the reducer's init (it reads LAST_SORT), so the mount
  // write is redundant. Only sync to the URL on a real user sort change.
  const didSyncSortRef = useRef(false);
  useEffect(() => {
    // Keep the session copy fresh regardless (cheap, no history impact) so
    // back/forward nav restores the sort via the reducer init.
    try { sessionStorage.setItem(CatalogLocalStorage.LAST_SORT, state.sortBy.value); } catch { /* ignore */ }
    if (!didSyncSortRef.current) {
      didSyncSortRef.current = true;
      return;
    }
    handleSetQuery({ sort: state.sortBy.value });
  }, [state.sortBy]);

  // Keep the active sort in sync with the URL ?sort= param. The reducer reads it
  // only once at init, so on browser back/forward (which restores the URL — e.g.
  // ?sort=random for Shuffle) the sort would otherwise fall back to the default.
  // Reapply it here so the sort + its order survive navigation. Guarded against a
  // loop with the writer above (only dispatches on a real difference).
  useEffect(() => {
    const fromUrl = query.sort ? sortValues(t).find((o) => o.value === query.sort) : undefined;
    if (fromUrl && fromUrl.value !== state.sortBy.value) {
      dispatchReducer({ type: ECatalogue.SortBy, payload: fromUrl });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.sort]);

  useEffect(() => {
    if (parseInt(jump as string) && catalogRef.current) {
      const { x, y } = catalogRef.current.getBoundingClientRect();
      scrollToAnchorByRef(catalogRef.current, x, y - 25);
    }
  }, []);

  // Sync search state with URL ?q= param.
  // Initial mount: restore from URL (e.g. artist link from favorites, or browser back to a search).
  // On subsequent qParam changes (browser back/forward): reflect the URL into local state.
  useEffect(() => {
    if (qParam && typeof qParam === "string" && qParam.length >= 3) {
      setLocalSearch(qParam);
      setSubmittedSearch(qParam);
      setSearchTrigger((prev) => prev + 1);
      dispatchReducer({ type: ECatalogue.CatalogTabToggle, payload: CatalogTab.TRACK });
      // Jump to the top of the results when a new search applies — e.g. tapping
      // an artist name while scrolled down a track list should show the results
      // from the top, not leave the user mid-page.
      requestAnimationFrame(() => {
        const el = catalogRef.current;
        if (el) {
          const { y } = el.getBoundingClientRect();
          scrollToAnchorByRef(el, 0, y + window.scrollY - 25);
        }
      });
    } else if (!qParam && submittedSearch) {
      // URL no longer carries ?q= but we have a submitted search → user navigated back
      // to the pre-search state. Clear the input + reset to normal catalog.
      setLocalSearch("");
      setSubmittedSearch("");
      allSmartResultsRef.current = [];
    }
  }, [qParam, tParam]); // submittedSearch intentionally omitted — read inside, would cause loops

  useEffect(() => {
    if (catalogRef.current && shouldJump.current) {
      const { y } = catalogRef.current.getBoundingClientRect();
      scrollToAnchorByRef(catalogRef.current, 0, y + window.scrollY - 25);
      shouldJump.current = false;
    }
  }, [query.pageNumber]);

  useEffect(() => {
    dispatchReducer({
      type: ECatalogue.Filters,
      payload: adaptFiltersForDisplay(query, tagState),
    });
  }, [tagState]);

  useEffect(() => {
    const filtersAndSort = {
      filter: query,
      sort: state.sortBy.sortValue,
    };
    if (filtersAndSort.filter.tags.length || filtersAndSort.filter.composer) {
      dispatch(
        availableTags({
          params: { ...filtersAndSort },
          isTrack: query.isAlbumsTab !== CatalogTab.ALBUM,
        }),
      );
    } else {
      syncDispatch(setAvailableTagsState(null));
    }
  }, [JSON.stringify(query?.tags), query?.isAlbumsTab]);

  useEffect(() => {
    dispatchReducer({
      type: ECatalogue.Filters,
      payload: adaptFiltersForDisplay(query, tagState),
    });
  }, [tagState]);

  useEffect(() => {
    if (tagState.length !== 0) {
      dispatchReducer({
        type: ECatalogue.Filters,
        payload: adaptFiltersForDisplay(query, tagState),
      });
    }
    dispatchReducer({
      type: ECatalogue.OpenFilters,
      payload: query?.isFiltersOpen,
    });
    if (!submittedSearch) {
      dispatchReducer({
        type: ECatalogue.CatalogTabToggle,
        payload: query?.isAlbumsTab,
      });
    }
    setPage(query?.pageNumber);

    const filtersAndSort = {
      filter: query,
      // Random: inject the session shuffle seed so the order is stable across
      // pages/navigation within the session (md5-ordered server-side).
      sort: state.sortBy.value === "random" ? { random: getShuffleSeed() } : state.sortBy.sortValue,
    };

    const getCatalogue = async () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      syncDispatch(setIsLoading(true));

      timeoutRef.current = setTimeout(
        async () => {
          isToggleBetween.current = false;

          if (state.catalogTab === CatalogTab.ALBUM) {
            return await dispatch(catalogue({ ...filtersAndSort, page, limit: CatalogMediaLimit.ALBUM }));
          }

          if (state.catalogTab === CatalogTab.QUICK_FILTERS && !location.pathname.includes(ERoutes.QuickFilter)) {
            await dispatch(categoriesFilterButtons({ sort: filtersAndSort.sort }));
            return await dispatch(quickFiltersCategories({ ...filtersAndSort, page }));
          }
          await dispatch(getAllTracks({ ...filtersAndSort, page, limit: CatalogMediaLimit.TRACK }));
        },
        isToggleBetween.current ? 0 : 500,
      );
    };

    // Only fetch regular catalog when NOT searching (smart search handles search results)
    if (!submittedSearch) {
      getCatalogue();
    }
  }, [
    state.sortBy,
    query?.isAlbumsTab,
    query?.pageNumber,
    JSON.stringify(query?.composer),
    JSON.stringify(query?.tags),
    query?.length?.max,
    query?.bpm?.max,
    query?.bpm?.min,
    query?.quickFiltersCategory,
    submittedSearch,
  ]);

  // Reset the paginated list to page 1 whenever the result set changes shape —
  // tags OR sort. getAllTracks ALWAYS appends to accumulatedTracks, so without
  // this a sort change would pile the newly-sorted page 1 onto the existing
  // list instead of replacing it. (Mount runs once regardless of dep count, so
  // adding sort only adds runs on an actual sort change.)
  const didResetPageRef = useRef(false);
  useEffect(() => {
    setPage(1);
    // Skip the URL write on the initial mount — pageNumber is already its
    // default (1), so writing it would only push a second history entry on top
    // of the artist-link `?q=…` entry (same Back-button fix as the sort sync
    // above). Real tag/sort changes still reset the page in the URL.
    if (!didResetPageRef.current) {
      didResetPageRef.current = true;
    } else {
      handleSetQuery({ pageNumber: 1 });
    }
    syncDispatch(resetAccumulatedTracks());
    sessionStorage.removeItem(CatalogLocalStorage.ACCUMULATED_TRACKS);
    sessionStorage.removeItem(CatalogLocalStorage.LAST_PAGE);
  }, [JSON.stringify(query?.tags), state.sortBy.value]);

  const sortSmartResults = (tracks: any[], sortBy: any) => {
    const sorted = [...tracks];
    const sortKey = Object.keys(sortBy.sortValue || {})[0];
    const sortDir = Object.values(sortBy.sortValue || {})[0] as string;
    if (!sortKey) return sorted;

    // Random: seeded client-side shuffle (server md5-order isn't available for
    // the semantic-search result set). Same seed → stable order this session.
    if (sortBy.value === "random") {
      const seed = getShuffleSeed();
      return sorted.sort((a, b) => seededHash(`${a._id || a.id}${seed}`) - seededHash(`${b._id || b.id}${seed}`));
    }

    const getVal = (track: any) => {
      if (sortKey === "views") return track.views || 0;
      if (sortKey === "downloads") return track.downloads || 0;
      if (sortKey === "createdAt") return new Date(track.createdAt).getTime();
      if (sortKey === "summary.length" || sortKey === "length") return track.length || 0;
      if (sortKey === "summary.bpm" || sortKey === "bpm") return track.bpm || 0;
      // 'trending' has no per-track score in the smart-search payload, so we
      // keep the search relevance order (all vals equal → stable).
      return 0;
    };

    sorted.sort((a, b) => {
      const aVal = getVal(a);
      const bVal = getVal(b);
      return sortDir === "DESC" ? bVal - aVal : aVal - bVal;
    });

    return sorted;
  };

  useEffect(() => {
    if (!submittedSearch) return;

    const runSmartSearch = async () => {
      syncDispatch(setIsLoading(true));
      setIsSearchLoading(true);
      setNoResultsQuery("");
      syncDispatch(resetAccumulatedTracks());
      setSmartResultsPage(1);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/smart-search/tracks?q=${encodeURIComponent(submittedSearch)}&limit=250`,
        );
        const data = await res.json();
        const tracks = Array.isArray(data) ? data : data?.data;
        trackSearch(submittedSearch, tracks?.length || 0);
        if (tracks?.length) {
          const sorted = sortSmartResults(tracks, state.sortBy);
          allSmartResultsRef.current = sorted;
          syncDispatch(restoreAccumulatedTracks(sorted.slice(0, SMART_PAGE_SIZE)));

          // Derive albums from track results (album is populated by the API)
          const albumMap = new Map<string, ICatalogue>();
          for (const track of tracks) {
            const aid = track.albumId || track.album?._id;
            if (!aid || albumMap.has(aid)) continue;
            const a = track.album || {};
            const albumTracks = tracks.filter((t: any) => (t.albumId || t.album?._id) === aid);
            albumMap.set(aid, {
              _id: aid,
              name: a.name || a.slug || "",
              slug: a.slug || aid,
              description: a.description || "",
              shortDescription: a.shortDescription || "",
              tracks: albumTracks,
              createdAt: a.createdAt || "",
              updatedAt: a.updatedAt || "",
              imageUrl: a.imageUrl || "",
              smallCoverUrl: track.smallCoverUrl || "",
              mediumCoverUrl: track.mediumCoverUrl || "",
              largeCoverUrl: track.largeCoverUrl || "",
              lowQualityZipUrl: "",
              zipUrl: "",
              summary: {
                composer: track.composer ? [typeof track.composer === "object" ? track.composer.name : track.composer] : [],
                bpm: [],
                tags: [],
                length: 0,
              },
              videoUrl: a.videoUrl || "",
              numTracks: a.numTracks || albumTracks.length,
            } as ICatalogue);
          }
          setSmartAlbums(Array.from(albumMap.values()));
        } else {
          // No tracks matched the (often over-constrained) query. Keep the
          // query in the box + show a notice, but fall back to the full catalog
          // so the page isn't empty.
          allSmartResultsRef.current = [];
          setSmartAlbums([]);
          setNoResultsQuery(submittedSearch);
          setSubmittedSearch(""); // render the regular catalog, not an empty smart list
          setSmartResultsPage(1);
          syncDispatch(resetAccumulatedTracks());
          setPage(1);
          await dispatch(getAllTracks({ filter: {}, sort: state.sortBy.sortValue, page: 1, limit: CatalogMediaLimit.TRACK }));
        }
      } catch {
        allSmartResultsRef.current = [];
        setSmartAlbums([]);
      } finally {
        syncDispatch(setIsLoading(false));
        setIsSearchLoading(false);
      }
    };

    runSmartSearch();
  }, [submittedSearch, searchTrigger]);

  // Track which search's results are on screen, so a result click (track
  // played/opened) is attributed to its search. Cleared when search is cleared.
  useEffect(() => {
    setActiveSearch(submittedSearch || null);
  }, [submittedSearch]);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    setNoResultsQuery("");

    // Clear any pending auto-submit
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }

    // If cleared, reset to the normal catalog + drop ?q= from URL.
    // Clearing submittedSearch re-runs the regular-catalog effect, but
    // getAllTracks ALWAYS appends to accumulatedTracks — so without resetting,
    // the default tracks just pile onto the lingering smart-search results.
    // Reset the list + page so the default catalog (in the selected sort)
    // replaces the search results cleanly.
    if (!value && submittedSearch) {
      setSubmittedSearch("");
      allSmartResultsRef.current = [];
      setSmartAlbums([]);
      syncDispatch(resetAccumulatedTracks());
      setPage(1);
      handleSetQuery({ pageNumber: 1 });
      if (qParam) setQParam(undefined, "replaceIn");
      return;
    }

    // No auto-submit — the user must press Enter to run a search
    // (handleSearchSubmit). Typing only updates the field + clears results
    // when emptied (handled above).
  };

  const handleSearchSubmit = () => {
    // Cancel pending auto-submit since user hit enter
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    if (localSearch.length >= 3) {
      setSubmittedSearch(localSearch);
      setSearchTrigger((prev) => prev + 1);
      // Push to URL so browser back/forward restores the search.
      setQParam(localSearch, "pushIn");
    }
  };

  const handleSmartLoadMore = () => {
    const nextPage = smartResultsPage + 1;
    const sorted = sortSmartResults(allSmartResultsRef.current, state.sortBy);
    const nextSlice = sorted.slice(0, nextPage * SMART_PAGE_SIZE);
    syncDispatch(restoreAccumulatedTracks(nextSlice));
    setSmartResultsPage(nextPage);
  };

  // Re-sort smart search results when sort changes
  useEffect(() => {
    if (!submittedSearch || !allSmartResultsRef.current.length) return;
    const sorted = sortSmartResults(allSmartResultsRef.current, state.sortBy);
    allSmartResultsRef.current = sorted;
    syncDispatch(restoreAccumulatedTracks(sorted.slice(0, smartResultsPage * SMART_PAGE_SIZE)));
  }, [state.sortBy.value, submittedSearch, smartResultsPage]);
  const handleResetFilters = () => {
    handleSetQuery(null);
  };

  const handleSetFilters = (item: string[]) => {
    dispatchReducer({
      type: ECatalogue.Filters,
      payload: item,
    });
    setPage(1);

    if (location.pathname.includes(ERoutes.QuickFilter)) {
      const params = buildUrl(
        [
          { key: "isAlbumsTab", value: CatalogTab.TRACK },
          { key: "isFiltersOpen", value: "1" },
        ],
        { tags: item },
      );

      navigate({
        pathname: ERoutes.Catalog,
        search: `?${params.toString()}`,
      });
      dispatchReducer({
        type: ECatalogue.CatalogTabToggle,
        payload: CatalogTab.TRACK,
      });
    }
  };

  const handleDeleteFilter = (option: string) => {
    const updatedFilters = state.filters.filter((el) => el !== option);
    dispatchReducer({
      type: ECatalogue.Filters,
      payload: updatedFilters,
    });

    const updatedValues = { ...query };

    if (updatedValues.tags.length !== 0) {
      updatedValues.tags = updatedValues.tags.map((el: string) => {
        let formattedValue: string = "";
        tagState.filter(({ _id }) => el === _id).forEach(({ value }) => (formattedValue = value));
        return formattedValue;
      });
    }

    Object.keys(updatedValues).forEach((key) => {
      if (Array.isArray(updatedValues[key])) {
        updatedValues[key] = updatedFilters.filter((filter) => {
          return updatedValues[key].includes(filter);
        });
      }
    });

    if (updatedValues.tags.length !== 0) {
      updatedValues.tags = updatedValues.tags.map((el: string) => {
        let formattedValue: string = "";
        tagState.filter(({ value }) => el === value).forEach(({ _id }) => (formattedValue = _id));
        return formattedValue;
      });
    }

    if (!updatedFilters.some((e) => REGEX_EXP_FOR_BPM.test(e))) {
      updatedValues.bpm = {};
    }

    if (!updatedFilters.some((e) => REGEX_EXP_FOR_LENGTH.test(e))) {
      updatedValues.length = {};
    }

    handleSetQuery(updatedValues);
  };

  const handleToggleModal = () => {
    if (!isDesktop) {
      return dispatchReducer({
        type: ECatalogue.OPEN_FILTER_DIALOG,
        payload: !state.openFilterDialog,
      });
    }
    dispatchReducer({
      type: ECatalogue.OpenFilters,
      payload: !state.isFiltersOpen,
    });
    handleSetQuery({ isFiltersOpen: !state.isFiltersOpen });
    localStorage.setItem(CatalogLocalStorage.FILTERS_SECTION, String(!state.isFiltersOpen));
  };

  const handleChange = (_event, value: number) => {
    setPage(value);
    handleSetQuery({ pageNumber: value });
    shouldJump.current = true;
  };

  const handleButtonPageChange = (isNext: boolean) => {
    let totalPages = 0;

    if (state.catalogTab === CatalogTab.QUICK_FILTERS && !location.pathname.includes(ERoutes.QuickFilter)) {
      totalPages = quickCategories.pageCount;
    } else {
      totalPages = tracksPageCount;
    }
    if (state.catalogTab === CatalogTab.ALBUM) {
      totalPages = pageCount;
    }

    let value = 1;
    if (isNext) {
      value = page + 1 > totalPages ? page : page + 1;
    } else {
      value = page - 1 < 1 ? page : page - 1;
    }
    setPage(value);
    handleSetQuery({ pageNumber: value });
  };

  const handleToggleSwitch = (value: CatalogTab) => {
    localStorage.setItem(CatalogLocalStorage.CATALOG_TAB, value);

    dispatchReducer({
      type: ECatalogue.CatalogTabToggle,
      payload: value,
    });
    setPage(1);
    syncDispatch(resetAccumulatedTracks());
    sessionStorage.removeItem(CatalogLocalStorage.ACCUMULATED_TRACKS);
    sessionStorage.removeItem(CatalogLocalStorage.LAST_PAGE);

    // During smart search, just switch content without URL navigation
    if (submittedSearch) {
      if (value === CatalogTab.TRACK && allSmartResultsRef.current.length) {
        setSmartResultsPage(1);
        syncDispatch(restoreAccumulatedTracks(allSmartResultsRef.current.slice(0, SMART_PAGE_SIZE)));
      }
      return;
    }

    const queryProps = { pageNumber: 1, isAlbumsTab: value };

    if (location.pathname.includes(ERoutes.QuickFilter)) {
      const params = buildUrl(
        [
          { key: "isAlbumsTab", value },
          { key: "isFiltersOpen", value: "1" },
        ],
        { tags: [] },
      );
      dispatchReducer({
        type: ECatalogue.Filters,
        payload: [],
      });
      navigate({
        pathname: ERoutes.Catalog,
        search: `?${params}`,
      });
    } else {
      handleSetQuery({
        ...queryProps,
        isFiltersOpen:
          (state.catalogTab === CatalogTab.ALBUM && value === CatalogTab.TRACK) ||
          (state.catalogTab === CatalogTab.TRACK && value === CatalogTab.ALBUM)
            ? state.isFiltersOpen
            : true,
      });
    }
    shouldJump.current = false;
  };

  return (
    <>
      <CatalogPageContext.Provider
        value={{
          state,
          dispatchReducer,
          t,
          isSmartSearch: !!submittedSearch,
          smartSearchHasMore: !!submittedSearch && smartResultsPage * SMART_PAGE_SIZE < allSmartResultsRef.current.length,
          smartAlbums,
          handleSmartLoadMore,
          localSearch,
          noResultsQuery,
          handlers: {
            handleToggleSwitch,
            handleSetFilters,
            handleToggleModal,
            handleSearchChange,
            handleSearchSubmit,
            handleResetFilters,
            handleDeleteFilter,
          },
        }}
      >
        <CustomHelmet t={tC} route={ERoutes.Catalog} />

        <PageContainer
          isPage
          withBanner={true}
          backgroundImage={isDesktop ? CatalogueBGDesktop : CatalogueBGMobile}
          withDivider
          style={{ padding: isDesktop ? `${isHeaderDesktop ? "80px" : "24px"} 0 0` : "24px 0 72px", backgroundSize: "100dvw", backgroundAttachment: "fixed" }}
          sx={{
            "&&": { minHeight: "100vh" },
            "&& > div": { alignSelf: "start" },
            "@keyframes fadeSlideIn": { from: { opacity: 0, transform: "translateY(24px)" }, to: { opacity: 1, transform: "translateY(0)" } },
            animation: "fadeSlideIn 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {isDesktop ? (
            <MainContentDesktop
              isCatalogueLoading={isCatalogueLoading}
              isSearchLoading={isSearchLoading}
              isToggleBetween={isToggleBetween}
              catalogRef={catalogRef}
              searchAnchorEl={searchAnchorEl}
              setSearchAnchorEl={setSearchAnchorEl}
            >
              <CatalogTabDesktop
                page={page}
                handleChange={handleChange}
                handleButtonPageChange={handleButtonPageChange}
              />
            </MainContentDesktop>
          ) : (
            <MainContentMobile isToggleBetween={isToggleBetween} catalogRef={catalogRef} isSearchLoading={isSearchLoading}>
              <CatalogTabMobile
                isCatalogueLoading={isCatalogueLoading}
                page={page}
                handleChange={handleChange}
                handleButtonPageChange={handleButtonPageChange}
              />
            </MainContentMobile>
          )}
        </PageContainer>
        <FAQ
          isMusicCatalog
          headerText={t("faq.mainText", { ns: "homePage" })}
          headerDescription={t("faq.description", { ns: "homePage" })}
        />
        {!isDesktop && (
          <Dialog open={state.openFilterDialog} handleClose={handleToggleModal} handleSetFilters={handleSetFilters} />
        )}
        <QuestionFlowDialog
          open={state.openQuestionDialog}
          onClose={() =>
            dispatchReducer({
              type: ECatalogue.OPEN_QUESTION_FLOW,
              payload: false,
            })
          }
        />
      </CatalogPageContext.Provider>
    </>
  );
};

export default Catalogue;
